import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { RootState, useSelector } from "src/store";

import { useGetRolloutDevicesQuery } from "@dashboard/rollouts/api";
import { RolloutDeviceSortCriteria, RolloutSortDirection } from "@dashboard/rollouts/api/rtkClient";
import { rolloutDeviceStatusLabel } from "@dashboard/rollouts/utils/rolloutStatus";
import { TableRow } from "@mui/material";
import { StatusHeader } from "@shared/table/headers/StatusHeader";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTable, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { CustomTableHeader } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { getErrorMessage } from "@shared/toasts/useQueryErrorToast";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { PaneElement } from "../shared/PaneElement";

import { columns, RolloutDeviceRow, selectRolloutDeviceRows } from "./RolloutDevicesTable.utils";

type Params = {
    pageIndex: number;
    pageSize: number;
    sortCriteria: RolloutDeviceSortCriteria;
    sortDirection: RolloutSortDirection;
    statuses: API.RolloutDeviceStatus[];
};

const defaultParams: Params = {
    pageIndex: 0,
    pageSize: 10,
    sortCriteria: "deviceid",
    sortDirection: "Ascending",
    statuses: [],
};

type Props = {
    rolloutId: number;
};

export const RolloutDevicesTable = ({ rolloutId }: Props) => {
    const { t } = useTranslation(["common"]);
    const [params, setParams] = React.useState<Params>(defaultParams);

    const { data: response, isFetching, isError, error, refetch } = useGetRolloutDevicesQuery({ id: rolloutId, ...params });
    const reports = React.useMemo(() => response?.list ?? [], [response]);
    const data = useSelector((state: RootState) => selectRolloutDeviceRows(state, reports));

    const onSort = (id: string) => {
        const sortCriteria = id as RolloutDeviceSortCriteria;
        setParams((prev) => ({
            ...prev,
            sortCriteria,
            sortDirection: prev.sortCriteria === sortCriteria && prev.sortDirection === "Ascending" ? "Descending" : "Ascending",
            pageIndex: 0,
        }));
    };

    const onStatusChange = (statuses: API.RolloutDeviceStatus[]) => {
        setParams((prev) => ({ ...prev, statuses, pageIndex: 0 }));
    };

    const table = useReactTable<RolloutDeviceRow>({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <PaneElement.TableLayout
            contentDeps={[params.pageIndex, params.pageSize, data.length]}
            pagination={
                <CustomTablePagination
                    component="div"
                    rowsPerPageOptions={[10, 50, 100, 200]}
                    count={response?.count ?? 0}
                    rowsPerPage={params.pageSize}
                    page={params.pageIndex}
                    onPageChange={(_event, pageIndex) => setParams((prev) => ({ ...prev, pageIndex }))}
                    onRowsPerPageChange={(e) =>
                        setParams((prev) => ({ ...prev, pageSize: Number(e.target.value), pageIndex: 0 }))
                    }
                />
            }
        >
            <MinimalTable stickyHeader>
                <MinimalTableHeader title={{ label: t("common:label.devices"), colSpan: columns.length }}>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <CustomTableHeader
                                    key={header.id}
                                    sortCriteria={params.sortCriteria}
                                    sortDirection={params.sortDirection === "Ascending" ? "asc" : "desc"}
                                    onSort={onSort}
                                    {...header}
                                >
                                    {header.column.id === "status" ? (
                                        <StatusHeader
                                            labels={_.mapValues(rolloutDeviceStatusLabel, (label) => label())}
                                            value={params.statuses}
                                            onChange={onStatusChange}
                                        />
                                    ) : null}
                                </CustomTableHeader>
                            ))}
                        </TableRow>
                    ))}
                </MinimalTableHeader>
                <TableBody
                    table={table}
                    loading={isFetching}
                    noDataMessage={t("common:table.noDevices")}
                    filter={{ isActive: params.statuses.length > 0, onResetFilter: () => onStatusChange([]) }}
                    error={{ isError, onError: () => void refetch(), message: getErrorMessage(error) }}
                >
                    {table.getRowModel().rows.map((row) => (
                        <HoverRow key={row.id} clickable={false}>
                            {row.getVisibleCells().map((cell) => (
                                <React.Fragment key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </React.Fragment>
                            ))}
                        </HoverRow>
                    ))}
                </TableBody>
            </MinimalTable>
        </PaneElement.TableLayout>
    );
};
